import {http, HttpResponse} from 'msw';

import {isNil} from 'ramda';

import {
  CheckoutProfileWriteSchema,
  UpdateCheckoutProfileArgSchema,
  CheckoutProfileIdsArgSchema,
  type CheckoutProfile,
  type CheckoutProfileWrite,
} from '../types';
import {getCheckoutProfileDocumentTypes} from '../utils/getCheckoutProfileDocumentCells';
import {
  checkoutProfileMockRefusal,
  type CheckoutProfileMockRefusal,
} from './checkoutProfileMockRefusal';
import type {CheckoutProfilesMockState} from './createCheckoutProfilesMockState';
import {getCheckoutProfileMissing} from './getCheckoutProfileMissing';
import {getCheckoutProfileMockShapeRefusals} from './getCheckoutProfileMockShapeRefusals';

const PROFILE_URL = '*/checkout/v1/checkout-profile';
const PROFILES_URL = '*/checkout/v1/checkout-profiles';

function profileWriteRefusals(
  state: CheckoutProfilesMockState,
  body: CheckoutProfileWrite,
  id?: string
) {
  const refusals: CheckoutProfileMockRefusal[] = [];
  if (
    [...state.profiles.values()].some(
      (profile) =>
        profile.id !== id && profile.branchId === body.branchId && profile.name === body.name
    )
  ) {
    refusals.push({code: 'PROFILE_NAME_NOT_UNIQUE', path: 'name', message: body.name});
  }
  const missing = getCheckoutProfileMissing(body);
  if (body.isActive && missing.length) {
    refusals.push({
      code: 'PROFILE_INCOMPLETE',
      path: 'isActive',
      message: `${body.name}: ${missing.map((item) => item.code).join(', ')}`,
      missing,
    });
  }
  const rules = [...state.rules.values()].filter(
    (rule) => rule.checkoutProfileId === id && rule.isActive
  );
  if (!body.isActive && rules.length) {
    refusals.push({
      code: 'PROFILE_USED_BY_ACTIVE_RULE',
      message: `${body.name}: ${rules.map((rule) => rule.name).join(', ')}`,
      rules,
    });
  }
  return refusals;
}

function buildProfile(
  state: CheckoutProfilesMockState,
  body: CheckoutProfileWrite,
  stored?: CheckoutProfile
): CheckoutProfile {
  const version = state.nextVersion();
  const missing = getCheckoutProfileMissing(body);
  return {
    ...body,
    id: stored?.id ?? crypto.randomUUID(),
    baseCurrency: stored?.baseCurrency ?? state.baseCurrency,
    version,
    isComplete: !missing.length,
    missing,
    activeDocumentTypes: getCheckoutProfileDocumentTypes(body),
    createdAt: stored?.createdAt ?? version,
    createdBy: stored?.createdBy ?? 'mock-administrator',
    updatedAt: version,
    updatedBy: 'mock-administrator',
  };
}

export function createCheckoutProfileHandlers(state: CheckoutProfilesMockState) {
  const stateChange = (isActive: boolean) =>
    http.post(`${PROFILES_URL}/${isActive ? 'activate' : 'deactivate'}`, async ({request}) => {
      const parsed = CheckoutProfileIdsArgSchema.safeParse(await request.json());
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'PROFILE_NAME_REQUIRED')
        );
      }
      const refusals: CheckoutProfileMockRefusal[] = [];
      const selected: CheckoutProfile[] = [];
      parsed.data.checkoutProfileIds.forEach((id, index) => {
        const profile = state.profiles.get(id);
        if (isNil(profile)) {
          refusals.push({
            code: 'PROFILE_NOT_FOUND',
            message: id,
            checkoutProfileId: id,
            path: `checkoutProfileIds[${index}]`,
          });
          return;
        }
        selected.push(profile);
        refusals.push(
          ...profileWriteRefusals(state, {...profile, isActive}, id).map((refusal) => ({
            ...refusal,
            checkoutProfileId: id,
            path: `checkoutProfileIds[${index}]`,
          }))
        );
      });
      if (refusals.length) {
        return checkoutProfileMockRefusal(refusals);
      }
      selected.forEach((profile) =>
        state.profiles.set(profile.id, {...profile, isActive, version: state.nextVersion()})
      );
      return new HttpResponse(null, {status: 204});
    });

  return [
    http.get(PROFILES_URL, ({request}) => {
      const search = new URL(request.url).searchParams;
      const profiles = [...state.profiles.values()]
        .filter(
          (profile) =>
            (!search.has('branchId') || profile.branchId === search.get('branchId')) &&
            (!search.has('typeOfSale') || profile.typeOfSale === search.get('typeOfSale')) &&
            (!search.has('isActive') || String(profile.isActive) === search.get('isActive'))
        )
        .sort((first, second) => first.name.localeCompare(second.name))
        .map(({id, name, branchId, typeOfSale, isActive, isComplete}) => ({
          id,
          name,
          branchId,
          typeOfSale,
          isActive,
          isComplete,
        }));
      return HttpResponse.json(profiles);
    }),
    http.get<{checkoutProfileId: string}>(`${PROFILE_URL}/:checkoutProfileId`, ({params}) => {
      const profile = state.profiles.get(params.checkoutProfileId);
      return !isNil(profile)
        ? HttpResponse.json(profile)
        : checkoutProfileMockRefusal(
            [{code: 'PROFILE_NOT_FOUND', message: params.checkoutProfileId}],
            404
          );
    }),
    http.post(PROFILE_URL, async ({request}) => {
      const parsed = CheckoutProfileWriteSchema.safeParse(await request.json());
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'PROFILE_NAME_REQUIRED')
        );
      }
      const refusals = profileWriteRefusals(state, parsed.data);
      if (refusals.length) {
        return checkoutProfileMockRefusal(refusals);
      }
      const profile = buildProfile(state, parsed.data);
      state.profiles.set(profile.id, profile);
      return HttpResponse.json(profile, {status: 201});
    }),
    http.put<{checkoutProfileId: string}>(
      `${PROFILE_URL}/:checkoutProfileId`,
      async ({request, params}) => {
        const parsed = UpdateCheckoutProfileArgSchema.safeParse({
          checkoutProfileId: params.checkoutProfileId,
          body: await request.json(),
        });
        if (!parsed.success) {
          return checkoutProfileMockRefusal(
            getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'PROFILE_NAME_REQUIRED')
          );
        }
        const stored = state.profiles.get(params.checkoutProfileId);
        if (isNil(stored)) {
          return checkoutProfileMockRefusal(
            [{code: 'PROFILE_NOT_FOUND', message: params.checkoutProfileId}],
            404
          );
        }
        const {version, ...body} = parsed.data.body;
        if (version !== stored.version) {
          return checkoutProfileMockRefusal(
            [{code: 'PROFILE_VERSION_CONFLICT', message: stored.name}],
            409
          );
        }
        const immutable = (['branchId', 'typeOfSale'] as const).filter(
          (field) => body[field] !== stored[field]
        );
        if (immutable.length) {
          return checkoutProfileMockRefusal(
            immutable.map((path) => ({code: 'PROFILE_FIELD_IMMUTABLE', path, message: path}))
          );
        }
        const refusals = profileWriteRefusals(state, body, stored.id);
        if (refusals.length) {
          return checkoutProfileMockRefusal(refusals);
        }
        const profile = buildProfile(state, body, stored);
        state.profiles.set(profile.id, profile);
        return HttpResponse.json(profile);
      }
    ),
    stateChange(true),
    stateChange(false),
    http.post<{checkoutProfileId: string}>(
      `${PROFILE_URL}/:checkoutProfileId/delete`,
      ({params}) => {
        const profile = state.profiles.get(params.checkoutProfileId);
        if (isNil(profile)) {
          return checkoutProfileMockRefusal(
            [{code: 'PROFILE_NOT_FOUND', message: params.checkoutProfileId}],
            404
          );
        }
        const rules = [...state.rules.values()].filter(
          (rule) => rule.checkoutProfileId === profile.id
        );
        if (rules.length) {
          return checkoutProfileMockRefusal([
            {
              code: 'PROFILE_REFERENCED_BY_RULE',
              message: `${profile.name}: ${rules.map((rule) => rule.name).join(', ')}`,
              rules,
            },
          ]);
        }
        state.profiles.delete(profile.id);
        return new HttpResponse(null, {status: 204});
      }
    ),
  ];
}
